import type { HttpClient } from '@/lib/http/client.js'
import type { CreateOperatorInput, Operator, UpdateOperatorInput } from '@/types/operator/operator.type.js'

/**
 * `admin/users` and `admin/users/:id/*`, verified against `admin-user.controller.ts` and
 * `admin-user-access.controller.ts`.
 *
 * The list is **not paginated** — the backend returns the whole array — so filtering and
 * sorting are client-side. The operator population is tens, not thousands.
 */
export interface OperatorService {
  list: () => Promise<readonly Operator[]>
  get: (id: string) => Promise<Operator>
  create: (input: CreateOperatorInput) => Promise<Operator>
  update: (id: string, input: UpdateOperatorInput) => Promise<Operator>
  /** 204. Refused for your own account and for the last active super admin. */
  remove: (id: string) => Promise<void>
  /** PUT — replaces the whole set. */
  setRoles: (id: string, roles: readonly string[]) => Promise<Operator>
  /** PUT — replaces the whole set. Names outside the catalogue are a 422. */
  setPermissions: (id: string, permissions: readonly string[]) => Promise<Operator>
  /** Sets a password chosen by the administrator; the operator must replace it at sign-in. */
  setPassword: (id: string, password: string) => Promise<void>
  /** Bumps `token_epoch`: every session on every device dies. */
  revokeSessions: (id: string) => Promise<void>
  /** Clears their authenticator and recovery codes and signs them out everywhere. 204. */
  resetTwoFactor: (id: string) => Promise<void>
}

export function createOperatorService(http: HttpClient): OperatorService {
  return {
    list: () => http.get<readonly Operator[]>('admin/users'),
    get: (id) => http.get<Operator>(`admin/users/${encodeURIComponent(id)}`),
    create: (input) => http.post<Operator>('admin/users', input),
    update: (id, input) => http.patch<Operator>(`admin/users/${encodeURIComponent(id)}`, input),
    remove: async (id) => {
      await http.delete(`admin/users/${encodeURIComponent(id)}`)
    },
    setRoles: (id, roles) =>
      http.put<Operator>(`admin/users/${encodeURIComponent(id)}/roles`, { roles }),
    setPermissions: (id, permissions) =>
      http.put<Operator>(`admin/users/${encodeURIComponent(id)}/permissions`, { permissions }),
    setPassword: async (id, password) => {
      await http.post(`admin/users/${encodeURIComponent(id)}/password`, { password })
    },
    revokeSessions: async (id) => {
      await http.post(`admin/users/${encodeURIComponent(id)}/revoke-sessions`)
    },
    resetTwoFactor: async (id) => {
      await http.post(`admin/users/${encodeURIComponent(id)}/two-factor/reset`)
    },
  }
}
